import { useEffect } from 'react'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import getConfig from 'next/config'
import { useTranslation } from '@i18n'
import { publicUrl, appLinks } from '@libs/appUrl'
import { getApolloClient } from '@libs/getApolloClient'
import { getIntersectionArticleResult } from './getIntersectionArticleResult'
import { IntersectionArticle } from './IntersectionArticle'
import { useRouter } from 'next/router'
import pick from 'lodash/pick'
import qs from 'qs'

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = getApolloClient(ctx)
  const paths = ctx.params.paths as string[]
  const id = paths.join('.')
  const openWebLink = publicUrl(paths.join('/'))
  const article = await getIntersectionArticleResult({ client, id })
  const url = ctx.req.headers.referer || article.url

  return {
    props: {
      article,
      openWebLink,
      url,
    },
  }
}

export default function IntersectionArticleContainer({ article, openWebLink, url }) {
  const { t } = useTranslation('common')
  const { title, ogDescription, ogImage } = article
  const suffix = `${t('PopDaily')} ${t('波波黛莉')}`
  const ogTitle = `${title} | ${suffix}`
  const { publicRuntimeConfig } = getConfig()
  const router = useRouter()
  /**
   * 此頁提供 al:android:url 與 al:ios:url 讓 FB 開啟 APP
   * 所以才使用 client side redirection
   */
  useEffect(() => {
    if (publicRuntimeConfig.articleIntersectionEnabled) return
    /**
     * 中間頁關閉跳轉太快
     * 會導致中間頁的 GA 沒打到
     * 所以跳轉文章頁要帶上 utm
     */
    const utmQuery = qs.stringify(
      pick(router.query, ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']),
    )
    const queryString = utmQuery ? '?' + utmQuery : ''

    window.location.href = openWebLink + queryString
  }, [])

  return (
    <>
      <Head>
        <title key="title">{ogTitle}</title>
        <meta key="og:type" property="og:type" content="article" />
        <meta key="og:url" property="og:url" content={url} />
        <meta key="og:title" property="og:title" content={ogTitle} />
        <meta key="og:description" property="og:description" content={ogDescription} />
        <meta key="og:image" property="og:image" content={ogImage?.croppedUrl} />
        <meta key="og:image:width" property="og:image:width" content="1200" />
        <meta key="og:image:height" property="og:image:height" content="630" />
        <meta key="al:android:url" property="al:android:url" content={appLinks(url)} />
        <meta key="al:ios:url" property="al:ios:url" content={appLinks(url)} />
        {ogImage?.croppedUrl.startsWith('https') && (
          <meta
            key="og:image:secure_url"
            property="og:image:secure_url"
            content={ogImage?.croppedUrl}
          />
        )}
      </Head>
      {publicRuntimeConfig.articleIntersectionEnabled && (
        <IntersectionArticle article={article} openWebLink={openWebLink} />
      )}
    </>
  )
}
